<script setup>
import { clamp, isNil } from 'lodash'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import {
  AI_REPORT_CRITERION_LABEL_KEYS,
  AI_REPORT_ELEMENT_KEYS,
  AI_REPORT_ELEMENT_TYPE_ICONS,
  AI_REPORT_EMPTY_VALUE,
  AI_REPORT_KEYS,
  AI_REPORT_MASKED_ELEMENT_NAME,
  AI_REPORT_PAIR_KEYS,
  AI_REPORT_RECOMMENDATION_KEYS,
  AI_REPORT_REFERENCE_PLACEHOLDER_PATTERN,
  AI_REPORT_SCORE_GRADE_THRESHOLDS_PERCENT,
  AI_REPORT_SCORE_GRADES,
  AI_REPORT_SECTION_GROUP_KEYS,
  AI_REPORT_SECTION_KEYS
} from '@/utils/home-page/ai-report'
import {
  getAlignmentReportPairs,
  getAlignmentReportSections,
  isAlignmentReportEmpty
} from '@/utils/home-page/ai-report-filters'
import { MATH_SYMBOLS } from '@/utils/okr-elements/math'

import {
  HOMEPAGE_AI_ALIGNMENT_CHILD_SCORE_TEST_ID,
  HOMEPAGE_AI_ALIGNMENT_CHILD_TEST_ID,
  HOMEPAGE_AI_ALIGNMENT_ELEMENT_ICON_TEST_ID,
  HOMEPAGE_AI_ALIGNMENT_MASKED_ELEMENT_TEST_ID,
  HOMEPAGE_AI_ALIGNMENT_NO_PAIRS_TEST_ID,
  HOMEPAGE_AI_ALIGNMENT_NO_PARENT_TEST_ID,
  HOMEPAGE_AI_ALIGNMENT_OVERALL_SCORE_TEST_ID,
  HOMEPAGE_AI_ALIGNMENT_PARENT_SCORE_TEST_ID,
  HOMEPAGE_AI_ALIGNMENT_PARENT_TEST_ID,
  HOMEPAGE_AI_ALIGNMENT_RECOMMENDATION_CRITERION_TEST_ID,
  HOMEPAGE_AI_ALIGNMENT_RECOMMENDATION_ELEMENT_TEST_ID,
  HOMEPAGE_AI_ALIGNMENT_RECOMMENDATION_TEST_ID,
  HOMEPAGE_AI_ALIGNMENT_RECOMMENDATION_TEXT_TEST_ID,
  HOMEPAGE_AI_ALIGNMENT_REPORT_TEST_ID,
  HOMEPAGE_AI_ALIGNMENT_ROW_TEST_ID,
  HOMEPAGE_AI_ALIGNMENT_SCORE_TEST_ID,
  HOMEPAGE_AI_ALIGNMENT_SECTION_HEADING_TEST_ID,
  HOMEPAGE_AI_ALIGNMENT_SECTION_TEST_ID,
  HOMEPAGE_AI_ALIGNMENT_SUMMARY_TEST_ID,
  HOMEPAGE_AI_ALIGNMENT_WEAKEST_PAIRS_NOTE_TEST_ID
} from '@/components/home-page/jest-helpers'
import AppIcon from '@/components/ui/AppIcon/AppIcon'

defineOptions({
  name: 'HomePageAlignmentReport'
})

const props = defineProps({
  report: {
    type: Object,
    required: true
  }
})

const { t } = useI18n()

const COLUMNS = [
  { key: 'parent', labelKey: 'homepage.ai_reports.alignment.column.parent' },
  { key: 'child', labelKey: 'homepage.ai_reports.alignment.column.child' },
  { key: 'score', labelKey: 'homepage.ai_reports.alignment.column.score' },
  { key: 'recommendation', labelKey: 'homepage.ai_reports.alignment.column.recommendation' }
]

const COLUMNS_COUNT = COLUMNS.length

// a section without a group stands in a person's report and has no heading (FR-1.26)
const NO_GROUP_SECTION_KEY = 'no-group'

// the donut of the summary block (screens 1.1.3 and 2.1.2): a light track ring and the overall
// score as a thick arc with a purple-to-blue gradient, as the spinner of the dialog
const DONUT_SIZE = 72
const DONUT_STROKE_WIDTH = 8
const DONUT_CENTER = DONUT_SIZE / 2
const DONUT_RADIUS = DONUT_CENTER - DONUT_STROKE_WIDTH / 2
const DONUT_CIRCUMFERENCE = 2 * Math.PI * DONUT_RADIUS
const DONUT_VIEW_BOX = `0 0 ${DONUT_SIZE} ${DONUT_SIZE}`
const DONUT_GRADIENT_ID = 'hpalr-donut-gradient'
const DONUT_VALUE_STROKE = `url(#${DONUT_GRADIENT_ID})`

// the same size as $hpalr-element-icon-size of the styles, which aligns the "(no parent)" label
const ELEMENT_ICON_SIZE = 20

const { HIGH, MEDIUM, LOW } = AI_REPORT_SCORE_GRADES

const getScoreGrade = percent => {
  if (percent >= AI_REPORT_SCORE_GRADE_THRESHOLDS_PERCENT[HIGH]) {
    return HIGH
  }

  return percent >= AI_REPORT_SCORE_GRADE_THRESHOLDS_PERCENT[MEDIUM] ? MEDIUM : LOW
}

const getScorePercent = ({ score, maxScore }) => {
  return isNil(score) || !maxScore ? 0 : (score / maxScore) * 100
}

const getScoreText = ({ score, maxScore }) => {
  return isNil(score) || isNil(maxScore) ? AI_REPORT_EMPTY_VALUE : `${score}/${maxScore}`
}

const elements = computed(() => props.report[AI_REPORT_KEYS.ELEMENTS] ?? {})

const reportPairs = computed(() => getAlignmentReportPairs(props.report))

const isEmptyReport = computed(() => isAlignmentReportEmpty(props.report))

const hiddenPairsCount = computed(() => reportPairs.value.hiddenPairsCount)

// the overall score counts the rows without a parent as 0 and ignores the Home filters (FR-1.24)
const overallScorePercent = computed(() => {
  return clamp(props.report[AI_REPORT_KEYS.OVERALL_SCORE_PERCENT] ?? 0, 0, 100)
})

const overallScoreText = computed(() => `${overallScorePercent.value}${MATH_SYMBOLS.PERCENT}`)

const donutDashOffset = computed(() => {
  return DONUT_CIRCUMFERENCE * (1 - overallScorePercent.value / 100)
})

const getElementReference = elementId => {
  const element = elements.value[elementId]

  return isNil(element) ? '' : element[AI_REPORT_ELEMENT_KEYS.DISPLAY_ID] ?? ''
}

const createElementCell = ({ elementId, isParent = false } = {}) => {
  const element = elements.value[elementId] ?? {}
  const masked = Boolean(element[AI_REPORT_ELEMENT_KEYS.MASKED])

  // the glyph of the Element type stands before the name; a Masked element shows none (FR-0.25)
  const typeIcon = masked
    ? null
    : AI_REPORT_ELEMENT_TYPE_ICONS[element[AI_REPORT_ELEMENT_KEYS.TYPE]] ?? null

  const scoreText = getScoreText({
    score: element[AI_REPORT_ELEMENT_KEYS.SCORE],
    maxScore: element[AI_REPORT_ELEMENT_KEYS.MAX_SCORE]
  })

  // the "top-level" mark stands only under a parent without a parent in the input; the Element of
  // a "(no parent)" row shows its score alone in the Child column (FR-1.2, FR-1.15)
  const isTopLevel = isParent && isNil(element[AI_REPORT_ELEMENT_KEYS.PARENT_ELEMENT])

  return {
    displayId: element[AI_REPORT_ELEMENT_KEYS.DISPLAY_ID],
    name: masked ? AI_REPORT_MASKED_ELEMENT_NAME : element[AI_REPORT_ELEMENT_KEYS.NAME],
    masked,
    typeIcon,
    scoreText: isTopLevel
      ? t('homepage.ai_reports.alignment.score_top_level', { score: scoreText })
      : scoreText
  }
}

// a recommendation names the Element it is for and its criterion, and the placeholders of its
// text stand as Element references (FR-1.27)
const createRecommendation = ({ recommendation, pairId, index }) => {
  const criterion = recommendation[AI_REPORT_RECOMMENDATION_KEYS.CRITERION]
  const criterionLabelKey = AI_REPORT_CRITERION_LABEL_KEYS[criterion]
  const text = recommendation[AI_REPORT_RECOMMENDATION_KEYS.TEXT] ?? ''

  return {
    key: `${pairId}-${index}`,
    elementReference: getElementReference(recommendation[AI_REPORT_RECOMMENDATION_KEYS.ELEMENT_ID]),
    criterionLabel: criterionLabelKey ? t(criterionLabelKey) : criterion ?? '',
    text: text.replace(AI_REPORT_REFERENCE_PLACEHOLDER_PATTERN, '$1')
  }
}

const createRow = pair => {
  const pairId = pair[AI_REPORT_PAIR_KEYS.PAIR_ID]
  const parentId = pair[AI_REPORT_PAIR_KEYS.PARENT_ID]

  // a row without a parent shows "(no parent)" in place of the parent (FR-1.15)
  const parent = isNil(parentId) ? null : createElementCell({ elementId: parentId, isParent: true })
  const child = createElementCell({ elementId: pair[AI_REPORT_PAIR_KEYS.CHILD_ID] })

  // the recommendations of a row with a Masked element are hidden from the reader (FR-0.26)
  const hasMaskedElement = Boolean(parent?.masked) || child.masked

  const recommendations = hasMaskedElement
    ? []
    : (pair[AI_REPORT_PAIR_KEYS.RECOMMENDATIONS] ?? []).map((recommendation, index) => {
        return createRecommendation({ recommendation, pairId, index })
      })

  const alignmentScore = {
    score: pair[AI_REPORT_PAIR_KEYS.ALIGNMENT_SCORE],
    maxScore: pair[AI_REPORT_PAIR_KEYS.ALIGNMENT_MAX_SCORE]
  }

  return {
    pairId,
    elementCells: [
      {
        key: 'parent',
        testId: HOMEPAGE_AI_ALIGNMENT_PARENT_TEST_ID,
        scoreTestId: HOMEPAGE_AI_ALIGNMENT_PARENT_SCORE_TEST_ID,
        element: parent
      },
      {
        key: 'child',
        testId: HOMEPAGE_AI_ALIGNMENT_CHILD_TEST_ID,
        scoreTestId: HOMEPAGE_AI_ALIGNMENT_CHILD_SCORE_TEST_ID,
        element: child
      }
    ],
    alignmentScoreText: getScoreText(alignmentScore),
    alignmentScoreGrade: getScoreGrade(getScorePercent(alignmentScore)),
    recommendations
  }
}

const sections = computed(() => {
  return getAlignmentReportSections({
    report: props.report,
    pairs: reportPairs.value.pairs
  }).map(section => {
    const group = section[AI_REPORT_SECTION_KEYS.GROUP]

    return {
      key: isNil(group) ? NO_GROUP_SECTION_KEY : group[AI_REPORT_SECTION_GROUP_KEYS.GROUP_ID],
      groupName: isNil(group) ? '' : group[AI_REPORT_SECTION_GROUP_KEYS.GROUP_NAME],
      rows: section[AI_REPORT_KEYS.PAIRS].map(createRow)
    }
  })
})
</script>

<template>
  <div
    :data-auto-testid="HOMEPAGE_AI_ALIGNMENT_REPORT_TEST_ID"
    :data-testid="HOMEPAGE_AI_ALIGNMENT_REPORT_TEST_ID"
    class="hpalr-Report"
  >
    <div
      :data-auto-testid="HOMEPAGE_AI_ALIGNMENT_SUMMARY_TEST_ID"
      :data-testid="HOMEPAGE_AI_ALIGNMENT_SUMMARY_TEST_ID"
      class="hpalr-Summary"
    >
      <div class="hpalr-Donut">
        <svg
          :height="DONUT_SIZE"
          :viewBox="DONUT_VIEW_BOX"
          :width="DONUT_SIZE"
          aria-hidden="true"
          class="hpalr-Donut_Chart"
          focusable="false"
          xmlns="http://www.w3.org/2000/svg"
        >
          <defs>
            <linearGradient :id="DONUT_GRADIENT_ID" x1="0" x2="1" y1="0" y2="1">
              <stop class="hpalr-Donut_GradientStart" offset="0" />
              <stop class="hpalr-Donut_GradientEnd" offset="1" />
            </linearGradient>
          </defs>
          <circle
            :cx="DONUT_CENTER"
            :cy="DONUT_CENTER"
            :r="DONUT_RADIUS"
            :stroke-width="DONUT_STROKE_WIDTH"
            class="hpalr-Donut_Track"
          />
          <circle
            :cx="DONUT_CENTER"
            :cy="DONUT_CENTER"
            :r="DONUT_RADIUS"
            :stroke="DONUT_VALUE_STROKE"
            :stroke-dasharray="DONUT_CIRCUMFERENCE"
            :stroke-dashoffset="donutDashOffset"
            :stroke-width="DONUT_STROKE_WIDTH"
            class="hpalr-Donut_Value"
          />
        </svg>
        <span
          :data-auto-testid="HOMEPAGE_AI_ALIGNMENT_OVERALL_SCORE_TEST_ID"
          :data-testid="HOMEPAGE_AI_ALIGNMENT_OVERALL_SCORE_TEST_ID"
          class="hpalr-Donut_Label"
        >
          {{ overallScoreText }}
        </span>
      </div>
      <span class="hpalr-Summary_Caption">
        {{ t('homepage.ai_reports.alignment.overall_score_caption') }}
      </span>
    </div>

    <div
      v-if="isEmptyReport"
      :data-auto-testid="HOMEPAGE_AI_ALIGNMENT_NO_PAIRS_TEST_ID"
      :data-testid="HOMEPAGE_AI_ALIGNMENT_NO_PAIRS_TEST_ID"
      class="hpalr-Note"
    >
      {{ t('homepage.ai_reports.alignment.no_pairs_match') }}
    </div>

    <template v-else>
      <div
        v-if="hiddenPairsCount"
        :data-auto-testid="HOMEPAGE_AI_ALIGNMENT_WEAKEST_PAIRS_NOTE_TEST_ID"
        :data-testid="HOMEPAGE_AI_ALIGNMENT_WEAKEST_PAIRS_NOTE_TEST_ID"
        class="hpalr-Note"
      >
        {{ t('homepage.ai_reports.alignment.weakest_pairs_note', { count: hiddenPairsCount }) }}
      </div>

      <div class="hpalr-TableWrapper">
        <table class="hpalr-Table">
          <thead>
            <tr>
              <th
                v-for="column in COLUMNS"
                :key="column.key"
                :class="`hpalr-HeaderCell-${column.key}`"
                class="hpalr-HeaderCell"
              >
                {{ t(column.labelKey) }}
              </th>
            </tr>
          </thead>

          <tbody
            v-for="section in sections"
            :key="section.key"
            :data-auto-testid="HOMEPAGE_AI_ALIGNMENT_SECTION_TEST_ID"
            :data-testid="HOMEPAGE_AI_ALIGNMENT_SECTION_TEST_ID"
          >
            <tr v-if="section.groupName">
              <td
                :colspan="COLUMNS_COUNT"
                :data-auto-testid="HOMEPAGE_AI_ALIGNMENT_SECTION_HEADING_TEST_ID"
                :data-testid="HOMEPAGE_AI_ALIGNMENT_SECTION_HEADING_TEST_ID"
                class="hpalr-SectionHeading"
              >
                {{ section.groupName }}
              </td>
            </tr>

            <tr
              v-for="row in section.rows"
              :key="row.pairId"
              :data-auto-testid="HOMEPAGE_AI_ALIGNMENT_ROW_TEST_ID"
              :data-testid="HOMEPAGE_AI_ALIGNMENT_ROW_TEST_ID"
            >
              <td
                v-for="cell in row.elementCells"
                :key="cell.key"
                :data-auto-testid="cell.testId"
                :data-testid="cell.testId"
                class="hpalr-Cell"
              >
                <span
                  v-if="!cell.element"
                  :data-auto-testid="HOMEPAGE_AI_ALIGNMENT_NO_PARENT_TEST_ID"
                  :data-testid="HOMEPAGE_AI_ALIGNMENT_NO_PARENT_TEST_ID"
                  class="hpalr-NoParent"
                >
                  {{ t('homepage.ai_reports.alignment.no_parent') }}
                </span>

                <div v-else class="hpalr-Element">
                  <AppIcon
                    v-if="cell.element.typeIcon"
                    :data-auto-testid="HOMEPAGE_AI_ALIGNMENT_ELEMENT_ICON_TEST_ID"
                    :data-testid="HOMEPAGE_AI_ALIGNMENT_ELEMENT_ICON_TEST_ID"
                    :height="ELEMENT_ICON_SIZE"
                    :icon-name="cell.element.typeIcon"
                    :width="ELEMENT_ICON_SIZE"
                    class="hpalr-Element_Icon"
                  />
                  <div class="hpalr-Element_Body">
                    <div class="hpalr-Element_Title">
                      <span class="hpalr-Element_DisplayId">{{ cell.element.displayId }}</span>
                      <span
                        v-if="cell.element.masked"
                        :data-auto-testid="HOMEPAGE_AI_ALIGNMENT_MASKED_ELEMENT_TEST_ID"
                        :data-testid="HOMEPAGE_AI_ALIGNMENT_MASKED_ELEMENT_TEST_ID"
                        class="hpalr-Element_Name"
                      >
                        {{ cell.element.name }}
                      </span>
                      <span v-else class="hpalr-Element_Name">{{ cell.element.name }}</span>
                    </div>
                    <span
                      :data-auto-testid="cell.scoreTestId"
                      :data-testid="cell.scoreTestId"
                      class="hpalr-Element_Score"
                    >
                      {{ cell.element.scoreText }}
                    </span>
                  </div>
                </div>
              </td>

              <td class="hpalr-Cell hpalr-Cell-score">
                <span
                  :class="`hpalr-Score-${row.alignmentScoreGrade}`"
                  :data-auto-testid="HOMEPAGE_AI_ALIGNMENT_SCORE_TEST_ID"
                  :data-testid="HOMEPAGE_AI_ALIGNMENT_SCORE_TEST_ID"
                  class="hpalr-Score"
                >
                  {{ row.alignmentScoreText }}
                </span>
              </td>

              <td class="hpalr-Cell">
                <ul class="hpalr-Recommendations">
                  <li
                    v-for="recommendation in row.recommendations"
                    :key="recommendation.key"
                    :data-auto-testid="HOMEPAGE_AI_ALIGNMENT_RECOMMENDATION_TEST_ID"
                    :data-testid="HOMEPAGE_AI_ALIGNMENT_RECOMMENDATION_TEST_ID"
                    class="hpalr-Recommendations_Item"
                  >
                    <span class="hpalr-Recommendations_Meta">
                      <span
                        :data-auto-testid="HOMEPAGE_AI_ALIGNMENT_RECOMMENDATION_ELEMENT_TEST_ID"
                        :data-testid="HOMEPAGE_AI_ALIGNMENT_RECOMMENDATION_ELEMENT_TEST_ID"
                        class="hpalr-Recommendations_Element"
                      >
                        {{ recommendation.elementReference }}
                      </span>
                      <span
                        :data-auto-testid="HOMEPAGE_AI_ALIGNMENT_RECOMMENDATION_CRITERION_TEST_ID"
                        :data-testid="HOMEPAGE_AI_ALIGNMENT_RECOMMENDATION_CRITERION_TEST_ID"
                        class="hpalr-Recommendations_Criterion"
                      >
                        {{ recommendation.criterionLabel }}
                      </span>
                    </span>
                    <span
                      :data-auto-testid="HOMEPAGE_AI_ALIGNMENT_RECOMMENDATION_TEXT_TEST_ID"
                      :data-testid="HOMEPAGE_AI_ALIGNMENT_RECOMMENDATION_TEXT_TEST_ID"
                      class="hpalr-Recommendations_Text"
                    >
                      {{ recommendation.text }}
                    </span>
                  </li>
                </ul>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>
  </div>
</template>

<style lang="scss" scoped>
.hpalr-Report {
  display: flex;
  flex-direction: column;
  gap: 16px;
  font-family: $system-ui;
}

// the palette has no yellow variable; the design colours a medium score yellow
$hpalr-score-medium-color: #ffab00;

// the "(no parent)" label lines up with the names, which stand after the type glyph and its gap
$hpalr-element-icon-size: 20px;
$hpalr-element-icon-gap: 8px;

// the gap after the Alignment score before the "Recommendation" column (screens 1.1.3, 2.1.2)
$hpalr-score-column-gap: 48px;

.hpalr-Summary {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  border-radius: $border-radius-md-next;
  background-color: $azure-medium;
}

.hpalr-Summary_Caption {
  color: $dark-1;
  font-size: $fs-14;
  font-weight: fw('semi-bold');
  line-height: 20px;
}

.hpalr-Donut {
  display: flex;
  position: relative;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.hpalr-Donut_Chart {
  display: block;
}

.hpalr-Donut_Track {
  fill: none;
  stroke: $azure-dark;
}

.hpalr-Donut_Value {
  fill: none;
  stroke-linecap: round;
  transform: rotate(-90deg);
  transform-origin: center;
  transition: stroke-dashoffset $transition-normal;
}

.hpalr-Donut_GradientStart {
  stop-color: $purple;
}

.hpalr-Donut_GradientEnd {
  stop-color: $blue-9;
}

.hpalr-Donut_Label {
  position: absolute;
  color: $dark-1;
  font-size: $fs-16;
  font-weight: fw('bold');
  line-height: 24px;
}

.hpalr-Note {
  padding: 8px 12px;
  border-radius: $border-radius-sm-next;
  background-color: $grey-3-next;
  color: $dark-3;
  font-size: $fs-12;
  line-height: 16px;
}

.hpalr-TableWrapper {
  overflow-x: auto;
}

.hpalr-Table {
  width: 100%;
  border-collapse: collapse;
}

.hpalr-HeaderCell {
  padding: 0 12px 8px 0;
  border-bottom: 1px solid $grey-2-next;
  color: $grey-1-next;
  font-size: $fs-14;
  font-weight: fw('regular');
  line-height: 20px;
  text-align: left;
  white-space: nowrap;

  &-score {
    padding-right: $hpalr-score-column-gap;
  }

  &:last-child {
    padding-right: 0;
  }
}

.hpalr-SectionHeading {
  padding: 12px 0 4px;
  color: $dark-1;
  font-size: $fs-14;
  font-weight: fw('semi-bold');
  line-height: 20px;
}

.hpalr-Cell {
  padding: 10px 12px 10px 0;
  border-bottom: 1px solid $grey-2-next;
  vertical-align: top;

  &-score {
    padding-right: $hpalr-score-column-gap;
  }

  &:last-child {
    padding-right: 0;
  }
}

.hpalr-NoParent {
  display: block;
  padding-left: $hpalr-element-icon-size + $hpalr-element-icon-gap;
  color: $dark-3;
  font-size: $fs-14;
  line-height: 20px;
}

.hpalr-Element {
  display: flex;
  align-items: flex-start;
  gap: $hpalr-element-icon-gap;
  min-width: 160px;
}

.hpalr-Element_Icon {
  flex-shrink: 0;
  color: $grey-1-next;
}

.hpalr-Element_Body {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.hpalr-Element_Title {
  display: flex;
  align-items: baseline;
  gap: 6px;
  min-width: 0;
}

.hpalr-Element_DisplayId {
  flex-shrink: 0;
  color: $dark-3;
  font-size: $fs-12;
  font-weight: fw('semi-bold');
  line-height: 16px;
}

.hpalr-Element_Name {
  color: $dark-1;
  font-size: $fs-14;
  line-height: 20px;
}

.hpalr-Element_Score {
  color: $dark-3;
  font-size: $fs-12;
  line-height: 16px;
}

.hpalr-Score {
  font-size: $fs-20;
  font-weight: fw('semi-bold');
  line-height: 28px;
  white-space: nowrap;

  &-high {
    color: $grade-high-color-next;
  }

  &-medium {
    color: $hpalr-score-medium-color;
  }

  &-low {
    color: $grade-low-color-next;
  }
}

.hpalr-Recommendations {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin: 0;
  padding: 0;
  min-width: 200px;
  list-style: none;
}

.hpalr-Recommendations_Item {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.hpalr-Recommendations_Meta {
  display: flex;
  align-items: center;
  gap: 6px;
}

.hpalr-Recommendations_Element {
  color: $dark-3;
  font-size: $fs-12;
  font-weight: fw('semi-bold');
  line-height: 16px;
}

.hpalr-Recommendations_Criterion {
  padding: 0 6px;
  border-radius: $border-radius-sm;
  background-color: $grey-3-next;
  color: $dark-2;
  font-size: $fs-12;
  line-height: 16px;
}

// the text of a recommendation reads like the Element names (screens 1.1.3, 2.1.2)
.hpalr-Recommendations_Text {
  color: $dark-1;
  font-size: $fs-14;
  line-height: 20px;
}
</style>
